import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ErrorDetails, handleError } from '../errors/ErrorDetails';
import { getEnvironment } from '../environment/Environment';

export const HTTP_SUCCESS = 200;
export const HTTP_BAD_REQUEST = 400;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_FORBIDDEN = 403;
export const HTTP_NOT_FOUND = 404;
export const HTTP_METHOD_NOT_ALLOWED = 405;
export const HTTP_CONFLICT = 409;
export const HTTP_GONE = 410;
export const HTTP_INTERNAL_SERVER_ERROR = 500;

/**
 * RequestConfig is used to communicate desired behavior to the various
 * [[axiosInstance]] functions.
 */
export interface RequestConfig extends AxiosRequestConfig {
   /**
    * Set this option to true to force the underlying
    * framework to not handle errors and instead pass
    * the error up to the developer via the promise
    * catch clause.  This is generally not recommended.
    */
   disableErrorHandling?: boolean;

   /**
    * Set this option to true to force the underlying framework
    * to not handle client side errors (i.e. network errors,
    * connection timeouts) and instead pass the error up to the
    * developer via the promise catch clause.  This is generally
    * not recommended.
    */
   disableClientErrorHandling?: boolean;

   /**
    * Set this option to an array of error codes to force
    * the underlying framework to not handle server-side
    * errors for the error codes. The framework will
    * instead pass the error up to the developer via the
    * promise catch clause.  If this option is set to
    * true then no server side errors will be handled
    * by the underlying framework (404, 500, etc).
    */
   disableServerErrorHandling?: boolean | Array<number>;

   /**
    * Set this option to true to disable load masking
    * for long running client/server communications.
    */
   disableLoadMasking?: boolean;

   /**
    * A callback function that is called after the user
    * has acknowledged an error via the framework's
    * automatic error handling.  **The developer is
    * highly recommended to use this callback to make
    * sure that the user is not looking at a blank screen
    * after an error occurs.**
    */
   onErrorAcknowledged?: () => void;
}

/**
 * The configuration for the Axios interceptor for [[axiosInstance]]  Pass an instance
 * of this configuration to [[initializeAxios]] in order to receive interceptor events.
 */
export interface AxiosInterceptorConfig {
   /**
    * Return the default axios requestConfig
    * @returns {RequestConfig}
    */
   getDefaultRequestConfig(): RequestConfig;

   /**
    * A callback function to listen in on request start events
    *
    * @param {AxiosRequestConfig} config The AxiosRequestConfig object
    */
   requestStarted(config: AxiosRequestConfig): void;

   /**
    * A callback function to listen in on successfully completed request events
    *
    * @param {AxiosResponse} response The AxiosResponse object
    */
   requestCompletedSuccessfully(response: AxiosResponse): void;

   /**
    * A callback function to listen in on failed request events
    *
    * @param {AxiosError} error The AxiosError object
    */
   requestCompletedWithError(error: AxiosError): void;
}

let axiosInterceptorConfig: AxiosInterceptorConfig;

/**
 * Call this function to initialize the axios interceptor configuration for [[axiosInstance]]
 *
 * @param {AxiosInterceptorConfig} config The AxiosInterceptorConfig object
 */
export const initializeAxios = (config: AxiosInterceptorConfig): void => {
   axiosInterceptorConfig = config;
};

/**
 * Use this axios instance to get the benefits of the unifocus framework.  This includes, but is
 * not limited to:
 *
 * 1. Automated error handling and error details that include correlation-id, timestamp, userAgent,
 *    etc.
 * 2. Ability to override auto error handling for certain types of errors
 * 3. Interceptor event callbacks via [[AxiosInterceptorConfig]]
 *
 * ```typescript
 * const config: RequestConfig = {
 *    onErrorAcknowledged: () => {
 *        // Handle where to send user after they acknowledge an error so that they aren't
 *        // staring at a blank screen!
 *    };
 * };
 *
 * axiosInstance.get(rootUrl, config).then(response => {
 *
 *    // Do stuff here...
 *
 * }); // <-- We don't worry about having a catch clause on the promise because the underlying
 *     //     system pops up error dialog for us. See the onErrorAcknowledged comment above!
 * ```
 *
 * @type {AxiosInstance}
 */
export const axiosInstance: AxiosInstance = axios.create({
                                                            withCredentials: true
                                                         });
/**
 * Call this function to get specific error details.
 *
 * @param {AxiosError} axiosError The AxiosError object
 * @return {ErrorDetails} Converted error details
 */
export const convertAxiosErrorToErrorDetails = (axiosError: AxiosError): ErrorDetails => {
   const environment = getEnvironment();

   return {
      applicationId: environment.applicationId,
      buildNo: environment.buildNo,
      timeStamp: new Date().toISOString(),
      message: axiosError.message ? `AJAX Error: ${axiosError.message}` : 'AJAX Error',
      errorCode: axiosError.response && axiosError.response.status ? axiosError.response.status.toString() : undefined,
      requestMethod: axiosError.config.method ? axiosError.config.method.toUpperCase() : undefined,
      requestUrl: axiosError.config.url,
      userAgent: window.navigator.userAgent,
      correlationId: axiosError.response && axiosError.response.headers ? axiosError.response.headers['correlationId'] : undefined,
      partnerCode: environment.partnerCode,
      databaseId: environment.databaseId ? environment.databaseId.toString() : undefined,
      propertyId: environment.propertyId ? environment.propertyId.toString() : undefined,
      stackTrace: axiosError.response?.data
   };
};

const mergeRequestConfigWithDefaults = (config: AxiosRequestConfig) => {
   if (axiosInterceptorConfig) {
      return Object.assign({}, axiosInterceptorConfig.getDefaultRequestConfig(), config);
   }

   return Object.assign({}, config);
};

const requestOnFullfilled = (config: AxiosRequestConfig) => {
   const requestConfig = mergeRequestConfigWithDefaults(config);

   if (axiosInterceptorConfig) {
      axiosInterceptorConfig.requestStarted(requestConfig);
   }

   return requestConfig;
};

const requestOnRejected = (error: AxiosError) => {
   // Cannot test this code flow, see: https://github.com/axios/axios/issues/1359
   const requestConfig = mergeRequestConfigWithDefaults(error.config);
   const errorConfigAsAny = (requestConfig as any) || {};

   if (axiosInterceptorConfig) {
      axiosInterceptorConfig.requestCompletedWithError(error);
   }

   if (clientErrorHandlingDisabled(errorConfigAsAny)) {
      return Promise.reject(error);
   }

   handleError(convertAxiosErrorToErrorDetails(error), (error.config as any).onErrorAcknowledged);

   return Promise.reject(
      'The error was handled by the system. Use the onErrorAcknowledged callback to perform actions that should happen after the user acknowledges the error.');
};

const responseOnFullfilled = (response: AxiosResponse) => {
   if (axiosInterceptorConfig) {
      axiosInterceptorConfig.requestCompletedSuccessfully(response);
   }

   return response;
};

const responseOnRejected = (error: AxiosError) => {
   const requestConfig = mergeRequestConfigWithDefaults(error.config);
   const errorConfigAsAny = (requestConfig as any) || {};
   const statusCode = error.response ? error.response.status : 0;

   if (axiosInterceptorConfig) {
      axiosInterceptorConfig.requestCompletedWithError(error);
   }

   if (statusCode === HTTP_UNAUTHORIZED) {
      handleError(convertAxiosErrorToErrorDetails(error));
      return;
   }

   const disableClientErrorHandling = clientErrorHandlingDisabled(errorConfigAsAny);
   const disableServerErrorHandling = serverErrorHandlingDisabled(errorConfigAsAny, statusCode);

   if (disableClientErrorHandling && disableServerErrorHandling) {
      return Promise.reject(error);
   }

   if (statusCode > 0) {
      if (disableServerErrorHandling) {
         return Promise.reject(error);
      }
   }
   else {
      // status code <= 0 is assumed to be client side error
      // error.message === 'Network Error' || error.code === "ECONNABORTED" or whatever
      if (disableClientErrorHandling) {
         return Promise.reject(error);
      }
   }

   const onErrorAcknowledged = (error.config as any).onErrorAcknowledged
   handleError(convertAxiosErrorToErrorDetails(error), onErrorAcknowledged);

   if (!onErrorAcknowledged) {
      return Promise.reject(
         new Error('The error was handled by the system. Use the onErrorAcknowledged callback to perform actions that should happen after the user acknowledges the error.'));
   }
};

const disableServerErrorHandlingPropertyIncludesStatusCode = (disabledErrorHandlingStatusCodes: Array<number>,
                                                              statusCode: number) => {
   return disabledErrorHandlingStatusCodes.includes(statusCode);
};

const clientErrorHandlingDisabled = (errorConfigAsAny: any) => {
   return errorConfigAsAny.disableClientErrorHandling !== undefined
          ? errorConfigAsAny.disableClientErrorHandling
          : errorConfigAsAny.disableErrorHandling
};

const serverErrorHandlingDisabled = (errorConfigAsAny: any, statusCode: number) => {
   const disableServerErrorHandling = errorConfigAsAny.disableServerErrorHandling !== undefined
                                      ? errorConfigAsAny.disableServerErrorHandling
                                      : errorConfigAsAny.disableErrorHandling

   if (disableServerErrorHandling !== undefined) {
      if (Array.isArray(disableServerErrorHandling)) {
         return disableServerErrorHandlingPropertyIncludesStatusCode(disableServerErrorHandling,
                                                                     statusCode);
      }

      return disableServerErrorHandling;
   }

   return false;
};

axiosInstance.interceptors.request.use(requestOnFullfilled, requestOnRejected);
axiosInstance.interceptors.response.use(responseOnFullfilled, responseOnRejected);
